import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesDrawer({ open, onOpenChange, onRead }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const endRef = useRef(null);
  const countRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/messages`);
      setMessages(data);
      onRead?.();
      if (data.length !== countRef.current) {
        countRef.current = data.length;
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } catch { /* ignore */ }
  }, [onRead]);

  useEffect(() => {
    if (!open) return;
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [open, load]);

  const send = async () => {
    const text = body.trim();
    if (!text) { toast.error("Type a message first."); return; }
    try {
      const { data } = await axios.post(`${API}/me/messages`, { body: text });
      setMessages((m) => [...m, data]);
      countRef.current += 1;
      setBody("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Could not send message."); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#10161F] border-white/15 text-white p-0 flex flex-col" data-testid="messages-drawer">
        <SheetHeader className="p-5 border-b border-white/10">
          <SheetTitle className="font-display font-black uppercase flex items-center gap-2 text-white"><MessageSquare size={18} className="text-[#FF5E1E]" /> Messages</SheetTitle>
          <p className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase text-left">Direct line to Reentry Staffing</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-conversation">
          {messages.length === 0 && <div className="text-sm text-[#8FA3B8] text-center mt-8">No messages yet. Reach out — our team will reply here.</div>}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm ${m.sender === "user" ? "bg-[#FF5E1E] text-[#0B0F14]" : "bg-[#0B0F14] border border-white/10 text-[#F4F6F8]"}`}>
                <div>{m.body}</div>
                <div className={`font-mono-tech text-[8px] tracking-[0.1em] mt-1 ${m.sender === "user" ? "text-[#0B0F14]/75 font-semibold" : "text-[#8FA3B8]"}`}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-white/10 flex gap-2">
          <Textarea data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Type a message…" className="min-h-[44px] max-h-24 rounded-none bg-[#0B0F14] border-white/12 text-white resize-none" />
          <Button data-testid="message-send" onClick={send} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] h-auto px-4"><Send size={16} /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
